import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { SplashScreen } from '@capacitor/splash-screen';
import { useEffect, useRef } from 'react';

import { syncNativeStatusBar } from '@/core/lib/native-status-bar';

/**
 * Hook that initializes Capacitor.
 * Hides the splash screen once React has mounted and the page is rendering.
 * This hook runs in the root layout, so when it's called, the app is ready.
 * No need to check on individual pages - this handles it centrally.
 *
 * Note: Deep link handling is done in IonicRouterSetup where the router
 * is guaranteed to be ready.
 */
export function useCapacitorInit() {
  const appStateListenerRef = useRef<Awaited<
    ReturnType<typeof App.addListener>
  > | null>(null);

  useEffect(() => {
    const initCapacitor = async () => {
      try {
        // Check if we're running in Capacitor
        if (Capacitor.isNativePlatform()) {
          console.log('[CapacitorInit] Running in Capacitor - initializing...');

          // Wait a brief moment to ensure initial render is complete
          await new Promise((resolve) => setTimeout(resolve, 100));

          try {
            await Keyboard.setResizeMode({ mode: KeyboardResize.None });
          } catch (error) {
            console.warn(
              '[CapacitorInit] Failed to set keyboard resize mode:',
              error,
            );
          }

          // Capacitor hides the iOS form accessory bar by default. Multiline
          // fields need it: Return inserts a newline, so there is otherwise
          // no Done/Fertig control to dismiss the keyboard (DEV-337).
          try {
            if (Capacitor.getPlatform() === 'ios') {
              await Keyboard.setAccessoryBarVisible({ isVisible: true });
            }
          } catch (error) {
            console.warn(
              '[CapacitorInit] Failed to show keyboard accessory bar:',
              error,
            );
          }

          await syncNativeStatusBar();

          // Lock screen orientation to portrait
          try {
            await ScreenOrientation.lock({ orientation: 'portrait' });
            console.log(
              '[CapacitorInit] Screen orientation locked to portrait',
            );
          } catch (error) {
            console.warn(
              '[CapacitorInit] Failed to lock screen orientation:',
              error,
            );
          }

          console.log('[CapacitorInit] Hiding splash screen...');
          // Hide the splash screen with fade out animation
          await SplashScreen.hide({
            fadeOutDuration: 300,
          });
          console.log('[CapacitorInit] Splash screen hidden successfully');

          // Add app state listener
          appStateListenerRef.current = await App.addListener(
            'appStateChange',
            ({ isActive }) => {
              if (isActive) {
                console.log('[CapacitorInit] App is now active');
              } else {
                console.log('[CapacitorInit] App is now in background');
              }
            },
          );

          console.log(
            '[CapacitorInit] Capacitor initialized and splash screen hidden',
          );
        } else {
          console.log(
            '[CapacitorInit] Not running in Capacitor - skipping initialization',
          );
        }
      } catch (error) {
        console.error('[CapacitorInit] Error initializing Capacitor:', error);
        // Fallback: hide splash screen after delay even if there's an error
        try {
          if (Capacitor.isNativePlatform()) {
            setTimeout(async () => {
              try {
                console.log(
                  '[CapacitorInit] Fallback: Attempting to hide splash screen...',
                );
                await SplashScreen.hide({
                  fadeOutDuration: 300,
                });
                console.log('[CapacitorInit] Fallback: Splash screen hidden');
              } catch (e) {
                console.error(
                  '[CapacitorInit] Fallback: Error hiding splash screen:',
                  e,
                );
              }
            }, 1000);
          }
        } catch (e) {
          console.error('[CapacitorInit] Error in error recovery:', e);
        }
      }
    };

    initCapacitor();

    // Cleanup - remove only our specific listener
    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove().catch(() => {
          // Ignore cleanup errors
        });
        appStateListenerRef.current = null;
      }
    };
  }, []);
}
